import * as React from 'react';
import { styled } from '@glitz/react';
import { translate, useMatchSmallViewport } from '@avensia/excite';
import Image from 'webapp/components/image';
import { Theme } from 'webapp/theme';
import Select from 'svenssons/shared/fields/Select';
import Price from 'nordicnest/product/Price';
import { overflowEllipsisDecorator } from 'webapp/decorators/text';
import { createMaxSmallViewportDecorator } from 'webapp/viewport';
import { StockStatus } from 'svenssons/product/StockStatus';
import { Variant } from 'webapp/product/price-variant';
import { FieldSize } from 'webapp/components/field-size';
import Link from 'webapp/components/Link';
import { addLineItem } from '../checkout/commands';
import { Button, FeedbackStandaloneButton } from 'webapp/components/button';
import { createButtonHoverDecorator } from 'webapp/utils/button-hover';
import { CartIcon, CloseIcon } from 'webapp/components/icons';
import { a11yGrey, white } from 'webapp/shared/sharedTheme';
import { ChevronRight } from 'svenssons/shared/icons';
import { ZIndex } from 'webapp/z-index';
import { Overlay } from '@avensia/excite-ui';
import { bareListDecorator } from 'webapp/decorators/list';
import { overflowYDecorator } from 'webapp/decorators/overflow';
import { scrollbarDecorator } from './scrollbar';
import { ResetButton } from 'webapp/components/button/button';

function optionTransform(option: NordicNest.Api.SelectOptionApiModel & NordicNest.Api.PartialProductApiModel) {
  return (
    <Option to={option.url} onClick={e => e.stopPropagation()}>
      {option.images[0] ? <SelectedVariantImage image={option.images[0]} preset={'thumb'} /> : <NoImage />}
      <Info>
        <Text>{option.text}</Text>
        {option?.availableInCountry && option?.stockStatus && option.variantStatus ? (
          <StockStyle variantStatus={option.variantStatus} stockStatus={option.stockStatus} />
        ) : null}
      </Info>
      {option.availableInCountry && option && option?.price.current.inclVat !== 0 && (
        <CurrentPrice variant={Variant.Compact} price={option.price} />
      )}
      <AddToCartButton
        aria-label={translate(t => t.product.addToCartButtonText)}
        onClick={e => {
          e.preventDefault();
          return addLineItem(option.sku, 1, {
            eventData: {
              sku: option.sku,
              itemIdentifier: option.ticket,
              additionalProductAttributes: {
                ecommerce: { button_location: 'material_samples', button_name: 'add_to_cart' },
              },
            },
          });
        }}
        as={Theme.Transparent}
        fulfilledTheme={Theme.Positive}
        rejectedTheme={Theme.Negative}
        disableRippleEffect
      >
        <CartIcon />
      </AddToCartButton>
    </Option>
  );
}

function placeholder() {
  return (
    <Placeholder>
      <Title>{translate(t => t.product.materialSamplesTitle)}</Title>
      <Description>{translate(t => t.product.materialSamplesDescription)}</Description>
    </Placeholder>
  );
}

function MobileOptions(props: {
  options: (NordicNest.Api.SelectOptionApiModel & NordicNest.Api.PartialProductApiModel)[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) {
  const { options, isOpen, setIsOpen } = props;
  const isSmallViewport = useMatchSmallViewport();

  if (!isSmallViewport) return null;

  return (
    <Options zIndex={ZIndex.Overlay} onClose={() => setIsOpen(false)} isEnabled={isOpen}>
      <Container css={!isOpen && hiddenDecorator}>
        <Header>
          <Label>{translate(t => t.product.materialSamplesDescription)}</Label>
          <CloseButton
            as={Theme.Transparent}
            onClick={() => setIsOpen(false)}
            square
            aria-label={translate(t => t.ariaLabelCloseButton)}
          >
            <CloseIcon />
          </CloseButton>
        </Header>
        <ScrollContainer>
          <List role="listbox">
            {options?.map(option => <MobileOption key={option.sku}>{optionTransform(option)}</MobileOption>)}
          </List>
        </ScrollContainer>
      </Container>
    </Options>
  );
}

export default function MaterialSamples(props: {
  materialSamples: NordicNest.Api.PartialProductApiModel[] | undefined;
}) {
  const isSmallViewport = useMatchSmallViewport();
  const [isMobileOptionsOpen, setIsMobileOptionsOpen] = React.useState(false);

  if (!props.materialSamples) return null;

  const options =
    props.materialSamples &&
    props.materialSamples?.map(sample => ({
      ...sample,
      text: sample.displayName,
      value: sample.sku,
    }));

  return isSmallViewport ? (
    <>
      <MobileSelector onClick={() => setIsMobileOptionsOpen(true)}>
        {placeholder()}
        <ChevronRight />
      </MobileSelector>
      <MobileOptions options={options} isOpen={isMobileOptionsOpen} setIsOpen={setIsMobileOptionsOpen} />
    </>
  ) : (
    <VariantSelector
      as={Theme.White}
      size={FieldSize.Small}
      css={marginDecorator}
      options={options}
      optionTransform={optionTransform as (option: NordicNest.Api.SelectOptionApiModel) => React.ReactNode}
      placeholderTransform={placeholder}
      optionDecorator={optionPadding}
      selectedValueDecorator={paddingDecorator}
    />
  );
}

const CurrentPrice = styled(Price, {
  fontSize: t => t.sharedFontSize.xs,
  fontWeight: t => t.fontWeight?.bold,
  display: 'flex',
  alignItems: 'center',
});

const optionPadding = styled({
  padding: { xy: t => t.sharedSpacing.s },
});

const paddingDecorator = styled({
  padding: { x: t => t.sharedSpacing.m, y: t => t.sharedSpacing.s },
  position: 'relative',
});

const Text = styled.div(overflowEllipsisDecorator, {
  fontSize: t => t.sharedFontSize.xs,
  fontWeight: t => t.fontWeight?.normal,
});

const Info = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-around',
  overflow: 'hidden',
});

const VariantImage = styled(Image, {
  width: 40,
  height: 40,
  objectFit: 'contain',
});

const SelectedVariantImage = styled(
  VariantImage,
  createMaxSmallViewportDecorator({
    mixBlendMode: 'multiply',
  }),
);

const StockStyle = styled(StockStatus, {
  fontWeight: t => t.materialSamples?.stockStatusFontWeight,
});

const marginDecorator = styled({
  marginBottom: t => t.sharedSpacing.xl,
});

const VariantSelector = styled(Select, {
  display: 'flex',
});

const Option = styled(
  Link,
  createMaxSmallViewportDecorator({
    fontSize: t => t.sharedFontSize.s,
    padding: { x: t => t.sharedSpacing.s, y: t => t.sharedSpacing.m },
  }),
  {
    fontSize: t => t.sharedFontSize.xs,
    display: 'grid',
    gridTemplateColumns: '40px 1fr auto auto',
    alignItems: 'center',
    gap: t => t.sharedSpacing.m,
    minHeight: 40,
  },
);

const Placeholder = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',

  ':before': {
    content: '""',
    position: 'absolute',
    left: -1,
    top: -1,
    height: 'calc(100% + 2px)',
    width: 4,
    backgroundColor: t => t.actionButton.color,
  },
});

const Title = styled.div({
  fontSize: t => t.sharedFontSize.xs,
  fontWeight: t => t.materialSamples?.titleFontWeight,
  color: 'black',
});

const Description = styled.div({
  fontSize: t => t.sharedFontSize.xs,
  fontWeight: t => t.materialSamples?.descriptionFontWeight,
  color: a11yGrey,
});

const buttonDecorator = styled({
  padding: { xy: t => t.sharedSpacing.xs },
  fontSize: t => t.sharedFontSize.xxs,
  border: { xy: { width: 0 } },
  borderRadius: '50%',
});

const AddToCartButton = styled(
  FeedbackStandaloneButton,
  buttonDecorator,
  createButtonHoverDecorator({
    backgroundColor: t => t.actionButton.color,
    color: t => t.actionButton.text,
  }),
);

const MobileSelector = styled(ResetButton, {
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: { x: t => t.sharedSpacing.m, y: t => t.sharedSpacing.s },
  border: { xy: { width: 'thin', style: 'solid', color: t => t.borderStrongColor } },
  position: 'relative',
  marginBottom: t => t.sharedSpacing.xl,
});

const Header = styled.div({
  backgroundColor: white,
  display: 'grid',
  gridTemplateAreas: '". label close"',
  gridTemplateColumns: '58px 2fr 58px',
  textAlign: 'center',
  fontSize: t => t.sharedFontSize.m,
  fontWeight: t => t.fontWeight?.normal,
  border: { bottom: { width: 1, style: 'solid', color: t => t.borderStrongColor } },
  alignItems: 'center',
  height: 72,
});

const Label = styled.div({
  fontSize: t => t.sharedFontSize.s,
  gridArea: 'label',
});

const CloseButton = styled(Button, {
  gridArea: 'close',
  padding: { x: t => t.sharedSpacing.m },
});

const hiddenDecorator = styled({
  display: 'none',
});

const Container = styled.div({
  position: 'absolute',
  width: '100%',
  bottom: 0,
  backgroundColor: white,
  overflow: 'hidden',
});

const ScrollContainer = styled.div(scrollbarDecorator, overflowYDecorator, {
  maxHeight: '55vh',
});

const List = styled.ul(bareListDecorator, {
  backgroundColor: white,
  width: '100%',
  height: 'inherit',
});

const Options = styled(Overlay);

const MobileOption = styled.li({
  borderBottom: { width: 'thin', style: 'solid', color: t => t.borderStrongColor },
  ':last-child': {
    borderBottom: { width: 0 },
  },
});

const NoImage = styled.div();
